import { ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAccountsByIds } from "@/hooks/api/use-accounts";
import { useExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import { formatFullDate } from "@/lib/date-formatting";
import {
  getExternalConnectionLink,
  getExternalSystemName,
} from "@/lib/external-connection-links";
import { AccountOpportunities } from "./account-opportunities-table";
import { AccountPersons } from "./account-persons-table";

export interface AccountDetailsDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  accountId?: string;
  onPersonClick?: (personId: string) => void;
  onDateClick?: (date: string) => void;
  onOpportunitySelect?: (opportunityId: string) => void;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-10">
      {/* Opportunities Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-32" /> {/* Section title */}
          <Skeleton className="h-6 w-20" /> {/* Count */}
        </div>
        <div className="rounded-lg border">
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-48" /> {/* Opportunity name */}
                  <Skeleton className="h-3 w-32" /> {/* Stage */}
                </div>
                <Skeleton className="h-4 w-24" /> {/* Date */}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Persons Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-32" /> {/* Section title */}
          <Skeleton className="h-6 w-20" /> {/* Count */}
        </div>
        <div className="rounded-lg border">
          <div className="p-4 space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-40" /> {/* Person name */}
                  <Skeleton className="h-3 w-56" /> {/* Email */}
                </div>
                <Skeleton className="h-4 w-24" /> {/* Role */}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AccountDetailsDialog({
  isOpen,
  onOpenChange,
  accountId,
  onPersonClick,
  onDateClick,
  onOpportunitySelect,
}: AccountDetailsDialogProps) {
  const { data: connections } = useExternalConnectionLinks();
  const { data: accounts } = useAccountsByIds(
    accountId ? [accountId] : undefined,
  );

  const account = accounts?.results.find((a) => a.id === accountId);

  const handlePersonClick = (personId: string) => {
    onPersonClick?.(personId);
    onOpenChange(false);
  };

  const handleDateClick = (date: string) => {
    onDateClick?.(date);
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[70rem] bg-card">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            {!account ? (
              <Skeleton className="h-8 w-64" />
            ) : (
              account?.name || "Account Details"
            )}
            {account &&
              (() => {
                const url = getExternalConnectionLink(account, connections);
                if (!url) return null;

                return (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ExternalLink className="h-5 w-5" />
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        View in{" "}
                        {getExternalSystemName(account.source_record_type)}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                );
              })()}
          </DialogTitle>
          <div className="text-sm text-muted-foreground mt-1">
            {!account ? (
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-32" /> {/* Website */}
                <span>•</span>
                <Skeleton className="h-4 w-24" /> {/* Type */}
                <span>•</span>
                <Skeleton className="h-4 w-48" /> {/* Created date */}
              </div>
            ) : (
              <>
                {account?.website} • {account?.type} • Created:{" "}
                {formatFullDate(account?.created_date || "")}
              </>
            )}
          </div>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(100vh-12rem)]">
          {!account ? (
            <LoadingSkeleton />
          ) : (
            <div className="space-y-10">
              {/* Related Data Sections */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <AccountOpportunities
                    accountId={account.id}
                    onDateClick={handleDateClick}
                    connections={connections}
                    onOpportunitySelect={onOpportunitySelect}
                  />
                </div>

                <div className="space-y-4">
                  <AccountPersons
                    accountId={account.id}
                    onPersonClick={handlePersonClick}
                  />
                </div>
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
